/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Settings → Session Rules (Figma). Read-only reference of the clinic's
 * session-status billing + therapist-pay policy (CR-005) plus the cancellation
 * window / buffer. The rules are policy constants enforced server-side.
 */
const STATUS_RULES = [
  { key: 'completed', dot: 'bg-emerald-500' },
  { key: 'noShow', dot: 'bg-rose-500' },
  { key: 'clientLate', dot: 'bg-amber-500' },
  { key: 'cancelledEarly', dot: 'bg-slate-400' },
  { key: 'cancelledLate', dot: 'bg-orange-500' },
  { key: 'consultantAttended', dot: 'bg-[var(--accent)]' },
] as const;

export function SessionRulesPanel(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
        <h3 className="font-semibold text-slate-800 dark:text-white">
          {t('settings.sessionRules.statusTitle')}
        </h3>
        <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
          {t('settings.sessionRules.statusSub')}
        </p>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {STATUS_RULES.map((r) => (
            <div key={r.key} className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:items-center">
              <span className="flex items-center gap-2 font-medium text-slate-800 dark:text-white">
                <span className={`h-2.5 w-2.5 rounded-full ${r.dot}`} aria-hidden="true" />
                {t(`settings.sessionRules.status.${r.key}.label`)}
              </span>
              <span className="text-sm text-slate-600 dark:text-slate-300">
                {t(`settings.sessionRules.status.${r.key}.billing`)}
              </span>
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {t(`settings.sessionRules.status.${r.key}.pay`)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
        <h3 className="mb-4 font-semibold text-slate-800 dark:text-white">
          {t('settings.sessionRules.cancelTitle')}
        </h3>
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs text-slate-400">{t('settings.sessionRules.cancelWindow')}</dt>
            <dd className="text-sm font-medium text-slate-800 dark:text-white">
              {t('settings.sessionRules.hours', { count: 24 })}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-400">{t('settings.sessionRules.buffer')}</dt>
            <dd className="text-sm font-medium text-slate-800 dark:text-white">
              {t('settings.sessionRules.minutes', { count: 15 })}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
